import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme, useCompany } from "@/lib/theme";
import { api, fileUrl } from "@/lib/api";
import AgreementsModal from "@/components/AgreementsModal";
import { APP_VERSION } from "@/lib/versions";
import { roleLabel } from "@/lib/constants";
import {
  HouseLine, Buildings, IdentificationCard, Package, Briefcase, Target,
  ListChecks, Airplane, Receipt, ClipboardText, Bell, ChartLineUp,
  ShieldCheck, GearSix, SignOut, MagnifyingGlass, List, X, UserCircle,
  Sun, Moon, Wallet, Fingerprint, Cube, Factory, FolderOpen, CaretDown,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ALL_ROLES = ["super_admin", "hr", "sales_manager", "department_manager", "employee", "finance_team"];

const NAV = [
  { key: "dashboard", to: "/", label: "Dashboard", icon: HouseLine, roles: "*" },
  { key: "opportunities", to: "/opportunities", label: "Opportunities", icon: Target, roles: "*" },
  { key: "accounts", to: "/accounts", label: "Customers", icon: Briefcase, roles: "*" },
  { key: "vendors", to: "/vendors", label: "Vendors", icon: Briefcase, roles: "*" },
  { key: "tasks", to: "/tasks", label: "Tasks", icon: ListChecks, roles: "*" },
  { key: "attendance", to: "/attendance", label: "Attendance", icon: Fingerprint, roles: "*" },
  { key: "inventory", to: "/inventory", label: "Inventory", icon: Cube, roles: "*" },
  { key: "products", to: "/products", label: "Products & Services", icon: Package, roles: "*" },
  { key: "travel", to: "/travel", label: "Travel Approval", icon: Airplane, roles: "*" },
  { key: "expenses", to: "/expenses", label: "Expenses", icon: Receipt, roles: "*" },
  { key: "project_expenses", to: "/project-expenses", label: "Project Expenses", icon: Receipt, roles: ["super_admin", "hr", "sales_manager"] },
  { key: "projects", to: "/projects", label: "Projects", icon: FolderOpen, roles: "*",
    children: [
      { to: "/projects", label: "All Projects" },
      { to: "/projects/dashboard", label: "Dashboard" },
      { to: "/projects/my", label: "My Projects" },
      { to: "/projects/calendar", label: "Calendar" },
      { to: "/projects/reports", label: "Reports" },
    ] },
  { key: "orders", to: "/orders", label: "Order Loading", icon: ClipboardText, roles: "*",
    children: [
      { to: "/orders", label: "Order Creation" },
    ] },
  { key: "approvals", to: "/approvals", label: "Approvals", icon: ClipboardText, roles: ["super_admin", "hr", "department_manager", "sales_manager"] },
  { key: "payroll", to: "/payroll", label: "Payroll", icon: Wallet, roles: "*" },
  { key: "forecast", to: "/forecast", label: "Forecast", icon: ChartLineUp, roles: ["super_admin", "hr"] },
  { key: "employees", to: "/employees", label: "Employees", icon: UserCircle, roles: ["super_admin", "hr"] },
  { key: "departments", to: "/departments", label: "Departments", icon: Buildings, roles: ["super_admin", "hr"] },
  { key: "designations", to: "/designations", label: "Designations", icon: IdentificationCard, roles: ["super_admin", "hr"] },
  { key: "audit", to: "/audit", label: "Audit Logs", icon: ShieldCheck, roles: ["super_admin"] },
  { key: "settings", to: "/settings", label: "Settings", icon: GearSix, roles: "*" },
];

// Default access map: role → true/false per module. Built from NAV.roles.
export const NAV_MODULES = NAV.map(({ key, label, roles }) => ({ key, label, defaults: roles === "*" ? ALL_ROLES : roles }));
export function defaultModuleAccess() {
  const map = {};
  NAV.forEach(({ key, roles }) => { map[key] = roles === "*" ? [...ALL_ROLES] : [...roles]; });
  return map;
}
// Fill in defaults for modules missing from the saved map and for roles never configured.
export function mergeModuleAccess(saved) {
  const defaults = defaultModuleAccess();
  if (!saved || !Object.keys(saved).length) return defaults;
  const out = {};
  Object.keys(defaults).forEach((k) => { out[k] = saved[k] ? [...saved[k]] : [...defaults[k]]; });
  ALL_ROLES.forEach((role) => {
    if (Object.values(saved).some((list) => (list || []).includes(role))) return;
    Object.keys(defaults).forEach((k) => {
      if (saved[k] && defaults[k].includes(role) && !out[k].includes(role)) out[k].push(role);
    });
  });
  return out;
}
export function canAccessModule(moduleAccess, moduleKey, role) {
  if (role === "super_admin") return true;   // super admin always sees everything
  return (mergeModuleAccess(moduleAccess)[moduleKey] || []).includes(role);
}

function ParentNav({ item, onNavigate }) {
  const location = useLocation();
  const activeParent = location.pathname.startsWith(item.to);
  const [expanded, setExpanded] = useState(activeParent);
  useEffect(() => { if (activeParent) setExpanded(true); }, [activeParent]);
  return (
    <div>
      <button type="button"
              onClick={() => setExpanded(!expanded)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-toggle`}
              className={`w-full nav-link flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${activeParent ? "active" : "text-white/55"}`}>
        <item.icon size={18} weight="duotone" />
        <span className="flex-1 text-left">{item.label}</span>
        <CaretDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="ml-6 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
          {item.children.map((c) => (
            <NavLink key={c.to} to={c.to} end onClick={onNavigate}
                     data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                     className={({ isActive }) => `block px-3 py-1.5 rounded-md text-xs ${isActive ? "text-orange-400 font-medium" : "text-white/50 hover:text-white/70"}`}>
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}


export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { company, logoUrl } = useCompany();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [moduleAccess, setModuleAccess] = useState(null);

  useEffect(() => {
    const load = () => api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/app-settings").then((r) => {
      if (cancelled) return;
      const m = r.data?.module_access;
      setModuleAccess(mergeModuleAccess(m));
    }).catch(() => setModuleAccess(defaultModuleAccess()));
    return () => { cancelled = true; };
  }, []);

  if (!user) return null;
  const visible = NAV.filter((n) => canAccessModule(moduleAccess, n.key, user.role));
  const brandName = company?.company_name || "SMIPL CRM";
  const tagline = company?.tagline || "Enterprise Suite";

  return (
    <div className="min-h-screen flex" style={{ background: "hsl(var(--background))" }}>
      <AgreementsModal />
      {/* Sidebar */}
      <aside
        className={`app-sidebar fixed lg:sticky lg:top-0 lg:h-screen lg:self-start inset-y-0 left-0 z-50 w-64 flex flex-col transform transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="h-16 px-5 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-2.5 min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="h-9 w-9 object-contain rounded-md" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-[#fd7e14] flex items-center justify-center text-white font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                {brandName.charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-white font-semibold text-base tracking-tight truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{brandName}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 truncate">{tagline}</div>
            </div>
          </div>
          <button className="lg:hidden text-white/60" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5" data-testid="sidebar-nav">
          {visible.map((n) => n.children ? (
            <ParentNav key={n.to} item={n} onNavigate={() => setOpen(false)} />
          ) : (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "active" : "text-white/55"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 text-xs text-white/40">
          Updated {new Date().toLocaleDateString("en-IN", { month: "short", year: "numeric" })} • {brandName}
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 sticky top-0 z-30 backdrop-blur border-b flex items-center gap-2 px-4 sm:px-6"
          style={{ background: "hsl(var(--card) / 0.95)", borderColor: "hsl(var(--border))" }}>
          <button className="lg:hidden mr-1" style={{ color: "hsl(var(--foreground))" }} onClick={() => setOpen(true)} data-testid="open-sidebar">
            <List size={22} />
          </button>
          <div className="hidden sm:flex items-center gap-2 rounded-md px-3 py-1.5 w-full max-w-md" style={{ background: "hsl(var(--muted))" }}>
            <MagnifyingGlass size={16} className="opacity-50" />
            <input
              data-testid="global-search-input"
              placeholder="Search opportunities, accounts, tasks…"
              className="bg-transparent outline-none text-sm w-full"
              style={{ color: "hsl(var(--foreground))" }}
            />
          </div>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              className="p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/5"
              title="Toggle theme"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              data-testid="notifications-button"
              onClick={() => navigate("/notifications")}
              className="relative p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/5"
            >
              <Bell size={20} />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#fd7e14] text-white text-[10px] font-semibold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="ml-1 flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-black/5 dark:hover:bg-white/5">
                  {user.profile_picture_file_id ? (
                    <img src={fileUrl(user.profile_picture_file_id)} alt={user.full_name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#fd7e14] text-white flex items-center justify-center text-sm font-semibold">
                      {(user.full_name || "?")[0]}
                    </div>
                  )}
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium leading-none">{user.full_name}</div>
                    <div className="text-xs opacity-60 mt-0.5 capitalize">{roleLabel(user.role)}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.full_name}</DropdownMenuLabel>
                <div className="px-2 pb-2 text-xs opacity-70">{user.mobile}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-link">
                  <GearSix size={16} className="mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={logout}>
                  <SignOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
